import {CheckCircleOutlined, CloseCircleOutlined, LoadingOutlined} from '@ant-design/icons/lib';
import {Button, Card, message, Modal, Space, Table, Tooltip, Typography} from 'antd';
import {TableRowSelection} from 'antd/es/table/interface';
import {
  useUpdateWebKeywordMutation,
  useWebKeywordsQuery,
  useWebReportQuery,
  WebKeyword,
  WebReportStatus
} from 'api/generated';
import {AnchorLink, Hint, PropertyList} from 'components';
import {formatUrl, formatWebsite} from 'lib/formaters';
import React, {useEffect, useState} from 'react';
import {SectionProps} from 'routes/Account/types';

const {Text} = Typography;

export const WebsiteReport: React.FC<Pick<SectionProps, 'account' | 'cardProps'>> = ({
  cardProps,
  account
}) => {
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [selectedRowKeys, setSelectedRowKeys] = useState<React.Key[]>([]);
  const [loading, setLoading] = useState(false);

  const businessWebsite = account?.business?.website;
  const cleanedUrl = businessWebsite ? new URL(businessWebsite).origin : '';

  const {data} = useWebReportQuery({
    skip: !cleanedUrl,
    variables: {
      baseUrl: cleanedUrl
    },
    context: {useApolloNetworkStatus: false}
  });

  const {data: keywordsData, refetch} = useWebKeywordsQuery({
    skip: !cleanedUrl,
    variables: {
      baseUrl: cleanedUrl
    }
  });

  const [updateWebKeyword] = useUpdateWebKeywordMutation();

  useEffect(() => {
    const keywords: WebKeyword[] = [...(keywordsData?.webKeywords?.items || [])];
    if (keywords) {
      const initialSelectedKeys = keywords
        .filter((keyword) => keyword.incorrect)
        .map((keyword) => keyword.urlKeyword);
      setSelectedRowKeys(initialSelectedKeys);
    }
  }, [keywordsData]);

  const handleIncorrectChange = async () => {
    setLoading(true);
    await Promise.all(
      keywords.map((keyword) => {
        return updateWebKeyword({
          variables: {
            input: {
              baseUrl: keyword.baseUrl,
              urlKeyword: keyword.urlKeyword,
              incorrect: selectedRowKeys.includes(keyword.urlKeyword)
            }
          }
        });
      })
    );
    refetch({baseUrl: cleanedUrl});
    setLoading(false);
    setIsModalOpen(false);
    message.success('Keywords updated');
  };

  const formatPageCountText = () => {
    return `${report?.pageCount} scanned${
      report?.failedPageCount ? `, ${report?.failedPageCount} failed` : ''
    }${report?.uncheckedPageCount ? `, ${report?.uncheckedPageCount} unchecked.` : '.'}`;
  };

  const formatRestrictedKeywordsText = () => {
    const incorrectKeywords = keywords.filter((keyword) => keyword.incorrect);
    const confirmedKeywords = keywords.filter((keyword) => keyword.confirmed);
    return `${keywords?.length} found${
      confirmedKeywords?.length > 0 ? `, ${confirmedKeywords?.length} confirmed` : ''
    }${incorrectKeywords?.length > 0 ? `, ${incorrectKeywords?.length} incorrect.` : '.'}`;
  };

  const onSelectChange = (newSelectedRowKeys: React.Key[]) => {
    setSelectedRowKeys(newSelectedRowKeys);
  };

  const rowSelection: TableRowSelection<any> = {
    columnTitle: (
      <Hint
        title="Incorrect"
        message="Mark this checkbox if the keyword is incorrectly evaluated by AI."
      />
    ),
    columnWidth: 110,
    selectedRowKeys,
    onChange: onSelectChange,
    getCheckboxProps: (_record) => ({
      disabled: loading
    })
  };

  if (!data || !keywordsData) return null;

  const report = data?.webReport;
  const keywords = [...keywordsData?.webKeywords?.items].sort(
    (a, b) => Number(b.confirmed) - Number(a.confirmed)
  );

  if (!account) return null;

  if (report?.status === WebReportStatus.PROCESSING) {
    return (
      <Card {...cardProps} title="Website report">
        <Text type="secondary">
          <LoadingOutlined style={{marginRight: 8}} /> Website scan in progress
        </Text>
      </Card>
    );
  }

  if (report?.status === WebReportStatus.FAILED) {
    return (
      <Card {...cardProps} title="Website report">
        <PropertyList
          properties={[
            {
              label: 'Website',
              value: formatWebsite(account?.business?.website)
            },
            {
              label: 'Error message',
              value: (
                <Text type="danger" strong>
                  {`${report.statusMessage || 'Website is not accessible'}`}
                </Text>
              )
            }
          ]}
        />
      </Card>
    );
  }

  return (
    <Card {...cardProps} title="Website report">
      <>
        <PropertyList
          omitEmpty
          properties={[
            {
              label: 'Website',
              value: formatWebsite(account?.business?.website)
            },
            {
              label: 'Title',
              value: report?.title
            },
            {
              label: 'Description',
              value: report?.description || null
            },
            {
              label: 'Summary',
              value: report?.summary || null
            },
            {
              label: 'Legal details url',
              value: formatUrl(report?.legalDetailsUrl)
            },
            {
              label: 'Risk category',
              value: report?.riskCategory || null
            },
            {
              label: 'Page count',
              value: formatPageCountText()
            },
            ...(keywords.length > 0
              ? [
                  {
                    label: 'Restricted keywords',
                    value: (
                      <>
                        {formatRestrictedKeywordsText()}{' '}
                        <AnchorLink onClick={() => setIsModalOpen(true)}>
                          View full report
                        </AnchorLink>
                      </>
                    )
                  }
                ]
              : [])
          ]}
        />
        <Modal
          bodyStyle={{padding: 0}}
          width={1100}
          centered
          title="Restricted keywords"
          open={isModalOpen}
          onCancel={() => setIsModalOpen(false)}
          footer={
            <Space>
              <Button onClick={() => setIsModalOpen(false)}>Cancel</Button>
              <Button type="primary" onClick={handleIncorrectChange} loading={loading}>
                Save
              </Button>
            </Space>
          }>
          <Table
            pagination={{
              style: {paddingRight: '16px'}
            }}
            rowSelection={rowSelection}
            dataSource={keywords}
            style={{marginTop: -1}}
            key="urlKeyword"
            rowKey={(item, _index) => item.urlKeyword}
            bordered
            columns={[
              {
                title: 'Keyword',
                dataIndex: 'keyword',
                key: 'keyword'
              },
              {
                title: 'Page url',
                dataIndex: 'url',
                key: 'url',
                render: (url) => formatUrl(url)
              },
              {
                title: 'Context',
                dataIndex: 'context',
                key: 'context',
                render: (_value, item) => {
                  const regex = new RegExp(`(${item.keyword})`, 'gi');
                  const highlightedText = item.context?.replace(regex, `<strong>$1</strong>`);
                  return (
                    <Tooltip title={item.explanation} overlayStyle={{maxWidth: 650}}>
                      <span dangerouslySetInnerHTML={{__html: highlightedText}} />
                    </Tooltip>
                  );
                }
              },
              {
                title: 'Confirmed by AI',
                dataIndex: 'confirmed',
                key: 'confirmed',
                align: 'center',
                width: 140,
                render: (confirmed) =>
                  confirmed ? (
                    <Text type="success" strong>
                      <CheckCircleOutlined /> Yes
                    </Text>
                  ) : (
                    <Text type="danger" strong>
                      <CloseCircleOutlined /> No
                    </Text>
                  )
              }
            ]}
          />
        </Modal>
      </>
    </Card>
  );
};
